import { useState } from "react";

export default function ReviewCard({ r }) {
  const [flipped, setFlipped] = useState(false);
  const name = r.customer?.name || "Anonymous";
  const avatar = r.customer?.avatar_url;
  const company = r.customer?.company;
  const title = r.customer?.title;
  const logo = r.customer?.company_logo_url;
  const linkedin = r.customer?.social?.linkedin;
  const sourceLink = pickSourceLink(r);

  const isLong = (r.word_count ?? 0) > 35 && r.text !== r.pull_quote;

  return (
    <div
      className="card-flip group"
      onClick={() => isLong && setFlipped((f) => !f)}
      role={isLong ? "button" : undefined}
      tabIndex={isLong ? 0 : -1}
      onKeyDown={(e) => {
        if (isLong && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          setFlipped((f) => !f);
        }
      }}
      aria-label={isLong ? "Tap to read full review" : undefined}
    >
      <div className={`card-flip-inner ${flipped ? "is-flipped" : ""}`}>
        {/* FRONT */}
        <article className="card-face card-face-front">
          <Stars rating={r.rating} />

          <div className="quote-mark" aria-hidden="true">"</div>
          <p className="card-quote">{r.pull_quote || r.text}</p>

          <Footer name={name} avatar={avatar} title={title} company={company} logo={logo} linkedin={linkedin} />

          {isLong ? (
            <div className="card-flip-hint">
              <span>Tap to read full review</span>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 5l7 7m0 0l-7 7m7-7H3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
          ) : null}
        </article>

        {/* BACK */}
        <article className="card-face card-face-back">
          <div className="flex items-start justify-between mb-3">
            <Stars rating={r.rating} />
            <button
              type="button"
              className="text-xs text-white/50 hover:text-flame transition flex items-center gap-1"
              onClick={(e) => { e.stopPropagation(); setFlipped(false); }}
              aria-label="Flip back"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M10 19l-7-7m0 0l7-7m-7 7h18" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Back
            </button>
          </div>

          {r.title ? <h3 className="font-semibold mb-2">{r.title}</h3> : null}
          <p className="card-full-text whitespace-pre-line">{r.text}</p>

          {r.tags?.length ? (
            <div className="flex flex-wrap gap-1.5 mt-4 mb-1">
              {r.tags.map((tag) => (
                <span key={tag} className="text-[10px] uppercase tracking-wide bg-white/5 border border-white/10 rounded-full px-2 py-0.5 text-white/60">
                  #{tag}
                </span>
              ))}
            </div>
          ) : null}

          <Footer name={name} avatar={avatar} title={title} company={company} logo={logo} linkedin={linkedin} sourceLink={sourceLink} />
        </article>
      </div>
    </div>
  );
}

function Stars({ rating }) {
  if (typeof rating !== "number" || rating <= 0) return null;
  return (
    <div className="flex gap-0.5 mb-3 text-flame" aria-label={`${rating} out of 5`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <span key={i} aria-hidden="true">{i < rating ? "★" : "☆"}</span>
      ))}
    </div>
  );
}

function Footer({ name, avatar, title, company, logo, linkedin, sourceLink }) {
  return (
    <div className="flex items-center gap-3 mt-5 pt-5 border-t border-white/10">
      {avatar ? (
        <img
          src={avatar}
          alt={name}
          className="w-10 h-10 rounded-full object-cover bg-white/5 flex-none"
          loading="lazy"
        />
      ) : (
        <div className="w-10 h-10 rounded-full bg-flame/30 grid place-items-center font-semibold flex-none">
          {name.charAt(0)}
        </div>
      )}
      <div className="text-sm flex-1 min-w-0">
        <div className="font-medium truncate">
          {linkedin ? (
            <a
              href={linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-flame transition"
              onClick={(e) => e.stopPropagation()}
              title="View LinkedIn profile"
            >
              {name}
            </a>
          ) : (
            name
          )}
        </div>
        <div className="text-white/50 truncate text-xs flex items-center gap-1.5">
          {logo ? (
            <img src={logo} alt="" className="w-3.5 h-3.5 rounded-sm bg-white/5" loading="lazy" />
          ) : null}
          <span className="truncate">
            {title}
            {title && company ? " · " : ""}
            {company}
          </span>
        </div>
      </div>
      {sourceLink ? (
        <a
          href={sourceLink.url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[11px] uppercase tracking-wide text-white/40 hover:text-flame transition flex-none"
          onClick={(e) => e.stopPropagation()}
          title={`View original on ${sourceLink.label}`}
        >
          {sourceLink.label} ↗
        </a>
      ) : null}
    </div>
  );
}

function pickSourceLink(r) {
  const url = r.source_url || "";
  const integration = (r.integration || "").toLowerCase();
  const PUBLIC_PLATFORMS = {
    linkedin: "LinkedIn",
    twitter: "Twitter",
    x: "X",
    "product hunt": "Product Hunt",
    google: "Google",
    facebook: "Facebook",
    reddit: "Reddit",
    capterra: "Capterra",
    g2: "G2",
    trustpilot: "Trustpilot",
    "app store": "App Store",
    youtube: "YouTube",
    tiktok: "TikTok",
    instagram: "Instagram",
    threads: "Threads",
  };
  if (PUBLIC_PLATFORMS[integration] && url && !/senja\.io/i.test(url)) {
    return { url, label: PUBLIC_PLATFORMS[integration] };
  }
  return null;
}
